import {curry} from 'ramda';
import {
    createAction,
    Action,
    ActionFunction1,
} from 'redux-actions';
import tryToCatch from 'try-to-catch';
import {setNotificationAction} from '@pluto/notification';
import {DispatchAny} from '@pluto/@types';
import {
    createQCJobTimespanMarkup,
    deleteQCJobTimespanMarkup,
    getQCJobTimespanMarkups,
    updateQCJobTimespanMarkup,
    TimespanMarker,
    TimespanMarkupBody,
    TimespanMarkupUpdateBody,
} from '@pluto/rest';

import {toggleSelectedMarkerAction} from 'VideoAudioQC/Timeline/Redux/TimelineActions';
import {
    MarkupMarker,
    PartialMarkupMarker,
    TimelineRowType,
} from 'VideoAudioQC/Timeline/TimelineTypes';
import {updateMarkerTimes} from './MarkupUtils';

export const MARKUP_SET = 'MARKUP_SET';
export const MARKUP_RESET = 'MARKUP_RESET';
export const MARKUP_ADD_MARKER = 'MARKUP_ADD_MARKER';
export const MARKUP_EDIT = 'MARKUP_EDIT';
export const MARKUP_DELETE = 'MARKUP_DELETE';

type MarkupPayload = Promise<MarkupMarker[]>;

export interface MarkupMarkerPayload {
    marker: MarkupMarker;
}

export interface EditMarkupMarkerPayload {
    markerData: PartialMarkupMarker;
}

export const parseMarkupMarker = (marker: TimespanMarker): MarkupMarker => {
    const {
        id,
        startFrameNumber,
        durationFrames,
        timespanCategoryId,
        createdByUser,
        updatedAt,
    } = marker;
    
    const start = startFrameNumber;
    const end = startFrameNumber + durationFrames;
    
    return {
        identifier: String(id),
        end,
        issue: timespanCategoryId,
        start,
        createdByUser,
        updatedAt,
    };
};

export const parseMarkupMarkers = (markers: TimespanMarker[]): MarkupMarker[] => {
    if (!markers)
        return [];
    
    return markers.map(parseMarkupMarker);
};

export const setMarkupAction = createAction<MarkupPayload, string, number, number>(MARKUP_SET, async (url: string, start: number, end: number): MarkupPayload => {
    const [error, markers] = await tryToCatch(getQCJobTimespanMarkups, url, {
        start,
        end,
    });
    
    if (!error) {
        return parseMarkupMarkers(markers);
    }
    
    console.error('Failed to fetch Markup markers.', error);
    
    return [];
});

export const resetMarkupAction = createAction<void>(MARKUP_RESET);

export const addMarkupMarkerAction = createAction(MARKUP_ADD_MARKER, (marker: MarkupMarker): MarkupMarkerPayload => {
    return {
        marker,
    };
});

export const createMarkupMarkerAction = curry(async (url: string, duration: number, rowId: string, markerData: MarkupMarker, dispatch: DispatchAny) => {
    const {issue} = markerData;
    const {
        start,
        end,
    } = updateMarkerTimes({
        marker: markerData,
        duration,
    }) as MarkupMarker;
    
    const body: TimespanMarkupBody = {
        startFrameNumber: start,
        durationFrames: end - start,
        timespanCategoryId: issue,
        subtype: 'markup',
    };
    
    const [error, markup] = await tryToCatch(createQCJobTimespanMarkup, url, body);
    
    if (!error) {
        const marker = parseMarkupMarker(markup);
        
        dispatch(addMarkupMarkerAction(marker));
        dispatch(toggleSelectedMarkerAction(marker.identifier, rowId, TimelineRowType.Markup));
        
        return;
    }
    
    dispatch(setNotificationAction({
        type: 'error',
        text: 'Failed to create Markup Note.',
    }));
    
    console.error('Failed to create Markup Note.', error);
});

export const editMarkupMarkerAction = createAction<EditMarkupMarkerPayload, PartialMarkupMarker>(MARKUP_EDIT, (markerData: PartialMarkupMarker): EditMarkupMarkerPayload => {
    return {
        markerData,
    };
});

export type UpdateMarkupMarkerActionProps = {
    url: string,
    duration: number,
    inpoint?: boolean,
    marker: PartialMarkupMarker,
};

export const updateMarkupMarkerAction = curry(async (props: UpdateMarkupMarkerActionProps, dispatch: DispatchAny) => {
    const {
        duration,
        marker,
        url,
        inpoint,
    } = props;
    const {
        identifier,
        issue,
    } = marker;
    const updatedMarker = updateMarkerTimes({
        marker,
        duration,
        inpoint,
    });
    
    dispatch(editMarkupMarkerAction(updatedMarker));
    
    const {
        start,
        end,
    } = updatedMarker;
    const body: TimespanMarkupUpdateBody = {
        subtype: 'markup',
    };
    
    if (start !== undefined && end !== undefined) {
        body.startFrameNumber = start;
        body.durationFrames = end - start;
    }
    
    if (issue !== undefined)
        body.timespanCategoryId = issue;
    
    const markerUrl = `${url}/${identifier}`;
    const [error, savedMarker] = await tryToCatch(updateQCJobTimespanMarkup, markerUrl, body);
    
    if (error) {
        dispatch(setNotificationAction({
            type: 'error',
            text: 'Failed to save Markup Note.',
        }));
        
        console.error('Failed to save Markup Note.', error);
        
        return;
    }
    
    const {updatedAt} = savedMarker;
    dispatch(editMarkupMarkerAction({
        ...updatedMarker,
        updatedAt,
    }));
});

export const deleteMarkupAction = createAction<string>(MARKUP_DELETE);

export const removeMarkupMarkerAction = curry(async (url: string, markerId: string, dispatch: DispatchAny) => {
    dispatch(deleteMarkupAction(markerId));
    
    const markerUrl = `${url}/${markerId}`;
    const [error] = await tryToCatch(deleteQCJobTimespanMarkup, markerUrl);
    
    if (error) {
        dispatch(setNotificationAction({
            type: 'error',
            text: 'Failed to delete Markup Note.',
        }));
        
        console.error('Failed to delete Markup Note.', error);
    }
});

export type SetMarkupAction = ActionFunction1<string, Action<MarkupPayload>>;
export type ResetMarkupAction = ActionFunction1<void, Action<void>>;

export type CreateMarkupMarkerAction = (url: string, duration: number, rowId: string, markerData: MarkupMarker) => Promise<void>;
export type UpdateMarkupMarkerAction = (props: UpdateMarkupMarkerActionProps) => Promise<void>;
export type RemoveMarkupMarkerAction = (url: string, markerId: string) => Promise<void>;
